import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import dayjs from 'dayjs';

import { useCompleteEncModalStore } from './CompleteEncModalStore';

import TrojanModalCover from 'components/TrojanModal/TrojanModalCover';
import { MLButton } from 'components/Button/Button';
import { usePutCompleteEncounter } from 'hooks/InteractiveMap/usePutCompleteEncounter';
import { CompleteEncounterFormValues, completeEncounterSchema } from 'api/encounters/types';
import MLTextArea from 'components/Input/TextArea';
import { MLInput } from 'components/Input/Input';
import { MLMultiSelect } from 'components/Select/MultiSelect';
import './CompleteEncModal.scss';

interface CompleteEncModalProps {
  refetchEncounters: () => void;
  vulnerabilities?: any[];
}

const getNowForDatetimeLocal = () => {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const CompleteEncModal: React.FC<CompleteEncModalProps> = ({ refetchEncounters, vulnerabilities = [] }) => {
  const { isOpen, onCompleteEncClose, encId } = useCompleteEncModalStore();
  const { slug } = useParams();
  const putCompleteEncounterMutation = usePutCompleteEncounter();

  const vulsOtions = vulnerabilities.map(vul => ({
    value: vul.id,
    label: `${vul.category} - ${dayjs(vul.create_date).format('DD.MM.YYYY')}`,
  }));

  const { control, handleSubmit, reset } = useForm<CompleteEncounterFormValues>({
    resolver: zodResolver(completeEncounterSchema),
    defaultValues: {
      fix_comment: '',
      fix_datetime: getNowForDatetimeLocal(),
      connected_vuls: [],
      photos: [],
    },
  });

  const onReset = useCallback(() => {
    reset();
    onCompleteEncClose();
  }, [reset, onCompleteEncClose]);

  if (!isOpen) return null;

  return (
    <TrojanModalCover
      size="md"
      onReset={onReset}
      title="Resolve sighting"
      footer={
        <>
          <MLButton color="danger" isGhost onClick={onReset} innerText="Close" />
          <MLButton
            color="success"
            isGhost
            onClick={handleSubmit(async formData => {
              putCompleteEncounterMutation.mutate(
                { encId, enc: formData, objectSlug: slug || '' },
                {
                  onSuccess: () => {
                    refetchEncounters();
                    onReset();
                  },
                },
              );
            })}
            innerText="Resolve sighting"
          />
        </>
      }
    >
      <>
        <div className="row mb-2 align-items-end">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="fix_datetime"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  {...field}
                  type="datetime-local"
                  error={error?.message}
                  label="Resolve Date & Time"
                  required
                  value={field.value ? field.value.substring(0, 16) : ''}
                  onChange={e => field.onChange(e.target.value)}
                />
              )}
            />
          </div>
        </div>
        <div className="row mb-2">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="connected_vuls"
              render={({ field, fieldState: { error } }) => (
                <MLMultiSelect
                  {...field}
                  error={error?.message}
                  label="Select Associated Vulnerabilities"
                  value={field.value}
                  onChange={field.onChange}
                  options={vulsOtions}
                  labelTitle="Select Vulnerabilities From List"
                />
              )}
            />
          </div>
        </div>
        <div className="row mb-3">
          <Controller
            name="photos"
            control={control}
            render={({ field }) => (
              <MLInput
                label="Photo"
                type="file"
                multiple
                onChange={e => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  field.onChange(files.length > 0 ? files : null);
                }}
              />
            )}
          />
        </div>
        <div className="row mb-2">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="fix_comment"
              render={({ field, fieldState: { error } }) => (
                <MLTextArea
                  required
                  {...field}
                  className="form-control"
                  error={error?.message}
                  label="Comment"
                  placeholder="Comment sighting and give advices to client"
                  rows={3}
                />
              )}
            />
          </div>
        </div>
      </>
    </TrojanModalCover>
  );
};

export default CompleteEncModal;
